import { useEffect, useState } from "react";
import { ArrowRight, Pencil, ShieldOff, Shield, Trash2, UsersRound } from "lucide-react";
import { PageHeader, StatusPill } from "../components/Primitives";
import { Modal, ConfirmDialog } from "../components/Modal";
import { api, errorText, formatDate } from "../lib/api";

export default function Staff() {
  const [staff, setStaff] = useState([]);
  const [modules, setModules] = useState([]);
  const [actions, setActions] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", mobile: "", password: "", permissions: {} });
  const [error, setError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", mobile: "", password: "", permissions: {} });

  const load = async () => {
    try {
      const res = await api.get("/staff");
      setStaff(res.data.data);
      setModules(res.data.modules);
      setActions(res.data.actions);
    } catch (err) {
      setError(errorText(err));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const togglePermission = (module, action) => {
    setForm((previous) => {
      const current = new Set(previous.permissions[module] || []);
      if (current.has(action)) current.delete(action);
      else current.add(action);
      return { ...previous, permissions: { ...previous.permissions, [module]: Array.from(current) } };
    });
  };

  const toggleEditPermission = (module, action) => {
    setEditForm((previous) => {
      const current = new Set(previous.permissions[module] || []);
      if (current.has(action)) current.delete(action);
      else current.add(action);
      return { ...previous, permissions: { ...previous.permissions, [module]: Array.from(current) } };
    });
  };

  const openEdit = (member) => {
    setEditing(member);
    setEditForm({
      name: member.name || "",
      mobile: member.mobile || "",
      password: "",
      permissions: member.permissions || {},
    });
  };

  const submitEdit = async (event) => {
    event.preventDefault();
    try {
      const payload = {
        name: editForm.name,
        mobile: editForm.mobile,
        permissions: editForm.permissions,
      };
      if (editForm.password) payload.password = editForm.password;
      await api.patch(`/staff/${editing.id}`, payload);
      setEditing(null);
      load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    try {
      await api.post("/staff", form);
      setShow(false);
      setForm({ name: "", email: "", mobile: "", password: "", permissions: {} });
      load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  const toggleStatus = async (member) => {
    try {
      await api.patch(`/staff/${member.id}`, { status: member.status === "ACTIVE" ? "BLOCKED" : "ACTIVE" });
      load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  const removeStaff = async () => {
    try {
      await api.delete(`/staff/${confirmDelete.id}`);
      setConfirmDelete(null);
      load();
    } catch (err) {
      setError(errorText(err));
      setConfirmDelete(null);
    }
  };

  return (
    <div className="page-enter">
      <PageHeader
        eyebrow="TEAM"
        title="Staff"
        description="Create team accounts, grant per-module permissions, block or remove access."
        action={
          <button className="button button-primary" onClick={() => setShow(true)} data-testid="add-staff-button">
            <UsersRound size={16} /> Add staff
          </button>
        }
      />
      {error ? <div className="error-message" data-testid="staff-error-message">{error}</div> : null}

      <section className="content-card table-card">
        <div className="table-toolbar">
          <div>
            <span className="card-kicker">STAFF ACCOUNTS</span>
            <h2>{staff.length} members</h2>
          </div>
        </div>
        <div className="table-wrap">
          <table data-testid="staff-table">
            <thead>
              <tr><th>Name</th><th>Email</th><th>Mobile</th><th>Permissions</th><th>Status</th><th>Created</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {staff.map((member) => (
                <tr key={member.id} data-testid={`staff-row-${member.id}`}>
                  <td><strong>{member.name}</strong></td>
                  <td>{member.email}</td>
                  <td>{member.mobile || "—"}</td>
                  <td>{Object.keys(member.permissions || {}).length} modules</td>
                  <td><StatusPill status={member.status} /></td>
                  <td>{formatDate(member.created_at)}</td>
                  <td>
                    <div className="action-row">
                      <button
                        type="button"
                        className="icon-button"
                        title="Edit staff"
                        onClick={() => openEdit(member)}
                        data-testid={`edit-staff-${member.id}`}
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        type="button"
                        className="icon-button"
                        title={member.status === "ACTIVE" ? "Block" : "Unblock"}
                        onClick={() => toggleStatus(member)}
                        data-testid={`toggle-staff-${member.id}`}
                      >
                        {member.status === "ACTIVE" ? <ShieldOff size={16} /> : <Shield size={16} />}
                      </button>
                      <button
                        type="button"
                        className="icon-button"
                        title="Remove"
                        onClick={() => setConfirmDelete(member)}
                        data-testid={`delete-staff-${member.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {show ? (
        <Modal title="New staff" eyebrow="ADD TEAM MEMBER" close={() => setShow(false)} testId="add-staff-modal" wide>
          <form className="modal-form grid-form" onSubmit={submit}>
            <label>Name<input type="text" required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} data-testid="staff-name-input" /></label>
            <label>Email<input type="email" required value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} data-testid="staff-email-input" /></label>
            <label>Mobile<input type="tel" value={form.mobile} onChange={(event) => setForm({ ...form, mobile: event.target.value })} data-testid="staff-mobile-input" /></label>
            <label>Password<input type="password" required minLength={6} value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} data-testid="staff-password-input" /></label>
            <div className="grid-span-2 permission-grid">
              <p className="hero-label">PERMISSIONS</p>
              <table className="permission-table">
                <thead><tr><th>Module</th>{actions.map((action) => <th key={action}>{action}</th>)}</tr></thead>
                <tbody>
                  {modules.map((module) => (
                    <tr key={module}>
                      <td>{module}</td>
                      {actions.map((action) => (
                        <td key={action}>
                          <input
                            type="checkbox"
                            checked={(form.permissions[module] || []).includes(action)}
                            onChange={() => togglePermission(module, action)}
                            data-testid={`permission-${module}-${action}`}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="modal-actions grid-span-2">
              <button type="button" className="button button-outline" onClick={() => setShow(false)}>Cancel</button>
              <button className="button button-primary" type="submit" data-testid="save-staff-button">
                Create staff <ArrowRight size={16} />
              </button>
            </div>
          </form>
        </Modal>
      ) : null}

      {confirmDelete ? (
        <ConfirmDialog
          title={`Remove ${confirmDelete.name}?`}
          message={`${confirmDelete.email} will lose access immediately. This cannot be undone.`}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={removeStaff}
          confirmLabel="Remove staff"
          danger
          testId="remove-staff-dialog"
        />
      ) : null}

      {editing ? (
        <Modal title={`Edit ${editing.name}`} eyebrow="UPDATE STAFF" close={() => setEditing(null)} testId="edit-staff-modal" wide>
          <form className="modal-form grid-form" onSubmit={submitEdit}>
            <label>Name<input type="text" required value={editForm.name} onChange={(event) => setEditForm({ ...editForm, name: event.target.value })} data-testid="edit-staff-name-input" /></label>
            <label>Mobile<input type="tel" value={editForm.mobile} onChange={(event) => setEditForm({ ...editForm, mobile: event.target.value })} data-testid="edit-staff-mobile-input" /></label>
            <label className="grid-span-2">New password (leave empty to keep current)<input type="password" minLength={6} value={editForm.password} onChange={(event) => setEditForm({ ...editForm, password: event.target.value })} data-testid="edit-staff-password-input" /></label>
            <div className="grid-span-2 permission-grid">
              <p className="hero-label">PERMISSIONS</p>
              <table className="permission-table">
                <thead><tr><th>Module</th>{actions.map((action) => <th key={action}>{action}</th>)}</tr></thead>
                <tbody>
                  {modules.map((module) => (
                    <tr key={module}>
                      <td>{module}</td>
                      {actions.map((action) => (
                        <td key={action}>
                          <input
                            type="checkbox"
                            checked={(editForm.permissions[module] || []).includes(action)}
                            onChange={() => toggleEditPermission(module, action)}
                            data-testid={`edit-permission-${module}-${action}`}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="modal-actions grid-span-2">
              <button type="button" className="button button-outline" onClick={() => setEditing(null)}>Cancel</button>
              <button className="button button-primary" type="submit" data-testid="update-staff-button">
                Save changes <ArrowRight size={16} />
              </button>
            </div>
          </form>
        </Modal>
      ) : null}
    </div>
  );
}
